import type {
  BranchChangesets,
  ConfigurationPackage,
  LayerTypesVersionId,
} from '@festina/configuration-general-types/configuration'
import {
  useCatchupBranch,
  useCreateOrUpdateConciseBranch,
} from '@festina-life/api'
import { assertNotEmpty, type Optional } from '@festina-life/core'
import { useCallback } from 'react'

import { useMetaModelContext } from '../../context/metaModelContext'
import { isProtectedBranchCategory } from '../../uiModels/branches/types'
import { useCreateTypesVersionFromFile } from '../../uiModels/branchTypes/useCreateTypesVersionFromFile'
import { convertConfigurationPackageToBranchChangesets } from '../../uiModels/helpers'
import { loadTypedDataFromFile } from './helpers'
import type { BranchCreationInput } from './types'

export const useCreateBranch = () => {
  const { mutateAsync: createBranch, isPending: isCreateBranchMutating } =
    useCreateOrUpdateConciseBranch()
  const { mutateAsync: createTypesPackage, isPending: isCreatingTypesVersion } =
    useCreateTypesVersionFromFile()
  const { mutateAsync: catchupBranch, isPending: isCatchingUp } =
    useCatchupBranch()
  const { blockTopology } = useMetaModelContext()
  const rootBlock = blockTopology.name

  const loadConfigurationPackage = useCallback(
    async (values: BranchCreationInput) => {
      if (values.importFromFile?.length) {
        const file = values.importFromFile.at(0)
        assertNotEmpty(file)

        return await loadTypedDataFromFile<
          ConfigurationPackage | BranchChangesets
        >(file, ['ConfigurationPackage', 'BranchChangesets'])
      } else if (values.importFromData) {
        return values.importFromData
      } else {
        return
      }
    },
    [],
  )

  const mutateAsync = useCallback(
    async (values: BranchCreationInput) => {
      let createdTypeVersions: Awaited<ReturnType<typeof createTypesPackage>>

      assertNotEmpty(values.server)

      const {
        typeVersionFile,
        typesVersionName,
        typesVersionDescription,
        typeVersionId: existentTypesVersionId,
      } = values

      if (existentTypesVersionId) {
        createdTypeVersions = existentTypesVersionId
      } else if (typesVersionName) {
        assertNotEmpty(typesVersionDescription)
        assertNotEmpty(typeVersionFile)

        createdTypeVersions = await createTypesPackage({
          typesVersionName,
          typeVersionFile,
          typesVersionDescription,
          server: values.server,
        })
      }

      const configurationData = await loadConfigurationPackage(values)
      const branchCreationData: Optional<BranchChangesets> =
        configurationData?._type === 'BranchChangesets'
          ? configurationData
          : convertConfigurationPackageToBranchChangesets(
              configurationData,
              rootBlock,
            )
      let typeVersions: LayerTypesVersionId[] | undefined

      if (createdTypeVersions && Array.isArray(createdTypeVersions)) {
        typeVersions = createdTypeVersions.map(([layerId, typesVersionId]) => ({
          _type: 'LayerTypesVersionId',
          typesVersionId,
          layer: layerId,
        }))
      } else if (createdTypeVersions) {
        typeVersions = [
          {
            _type: 'LayerTypesVersionId',
            typesVersionId: createdTypeVersions,
            layer: null,
          },
        ]
      }

      await createBranch({
        server: values.server,
        branchName: values.branchName,
        baseBranchName: values.baseBranch?.name,
        followsBranchName: values.followsBranch?.name,
        configurationData: branchCreationData,
        typeVersions,
        category: values.category,
      })

      if (values.followsBranch && !isProtectedBranchCategory(values.category)) {
        await catchupBranch({
          branchName: values.branchName,
          server: values.server,
        })
      }
    },
    [
      catchupBranch,
      createBranch,
      createTypesPackage,
      loadConfigurationPackage,
      rootBlock,
    ],
  )

  return {
    mutateAsync,
    isPending: isCreatingTypesVersion || isCreateBranchMutating || isCatchingUp,
  }
}
